<template>
    <div class="profile-page profile-page--form">
        <PageHeader icon="fas fa-user-edit">
            <template #title>Edit Profil</template>
            <template #subtitle>Perbarui identitas dan foto profil akun Anda.</template>
        </PageHeader>

        <Card card-class="profile-content-card profile-form-card" body-class="profile-content-card__body">
            <template #body>
                <b-form class="profile-form" @submit.prevent="formOnSubmit">
                    <div class="profile-form-layout">
                        <aside class="profile-photo-editor">
                            <div class="profile-avatar profile-avatar--large">
                                <img :src="previewPhoto" :alt="`Foto profil ${displayValue(form.name)}`" @error="useDefaultAvatar" />
                                <span class="profile-avatar__edit-icon"><i class="fas fa-camera"></i></span>
                            </div>
                            <h3>Foto Profil</h3>
                            <p>Gunakan foto yang jelas agar akun mudah dikenali.</p>
                            <clinical-file-upload
                                v-model="form.photo"
                                class="profile-photo-editor__input"
                                accept="image/*"
                                browse-text="Pilih Foto"
                                placeholder="Pilih foto baru"
                                drop-placeholder="Letakkan foto di sini"
                                @input="onFileUpload"
                            ></clinical-file-upload>
                            <small>Format JPG, JPEG, atau PNG. Maksimal 4 MB.</small>
                            <small v-if="error.photo" class="profile-photo-error text-danger">{{ error.photo }}</small>
                        </aside>

                        <div class="profile-form-main">
                            <div class="profile-section-heading">
                                <span class="profile-section-heading__icon"><i class="fas fa-id-card"></i></span>
                                <div>
                                    <h3>Informasi Utama</h3>
                                    <p>Pastikan nama dan email sudah sesuai.</p>
                                </div>
                            </div>

                            <div class="profile-fields-grid">
                                <b-form-group class="profile-field" label="Nama Lengkap" label-for="profile-name">
                                    <div class="profile-field__control">
                                        <i class="fas fa-user"></i>
                                        <b-form-input id="profile-name" v-model="form.name" required placeholder="Masukkan nama lengkap"></b-form-input>
                                    </div>
                                    <small v-if="error.name" class="text-danger">{{ error.name }}</small>
                                </b-form-group>

                                <b-form-group class="profile-field" label="Email" label-for="profile-email">
                                    <div class="profile-field__control">
                                        <i class="fas fa-envelope"></i>
                                        <b-form-input id="profile-email" v-model="form.email" type="email" required placeholder="nama@contoh.com"></b-form-input>
                                    </div>
                                    <small v-if="error.email" class="text-danger">{{ error.email }}</small>
                                </b-form-group>
                            </div>

                            <div v-if="showProfessionalData" class="profile-professional-data">
                                <div class="profile-section-heading profile-section-heading--compact">
                                    <span class="profile-section-heading__icon"><i class="fas fa-briefcase-medical"></i></span>
                                    <div>
                                        <h3>Informasi Pekerjaan</h3>
                                        <p>Data ini dikelola oleh administrator klinik.</p>
                                    </div>
                                </div>
                                <dl class="profile-details-grid profile-details-grid--compact">
                                    <div v-for="item in professionalDetails" :key="item.label" class="profile-detail-item">
                                        <span class="profile-detail-item__icon"><i :class="item.icon"></i></span>
                                        <div>
                                            <dt>{{ item.label }}</dt>
                                            <dd>{{ displayValue(item.value) }}</dd>
                                        </div>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div class="profile-form-actions">
                        <b-button class="profile-page__secondary-action" @click="$router.push('/profile/show')">Batal</b-button>
                        <b-button type="submit" class="profile-page__primary-action" :disabled="isLoading">
                            <i class="fas fa-save mr-2"></i>{{ isLoading ? "Menyimpan..." : "Simpan Perubahan" }}
                        </b-button>
                    </div>
                </b-form>
            </template>
        </Card>
    </div>
</template>

<script>
import Card from "@/view/content/Card.vue";
import module from "@/core/modules/CrudModule.js";
import validation from "@/core/modules/ValidationModule.js";
import { getUser, setUser } from "@/core/services/jwt.service";
import { UPDATE_PERSONAL_INFO } from "@/core/services/store/profile.module";

export default {
    components: { Card },
    data() {
        const user = getUser() || {};
        return {
            currentUser: user,
            completeData: {},
            defaultAvatar: "/media/users/user-default.svg",
            uploadedImageUrl: null,
            isLoading: false,
            error: {
                name: "",
                email: "",
                photo: "",
            },
            form: {
                name: user.name || "",
                email: user.email || "",
                photo: null,
                _method: "PUT",
            },
        };
    },
    computed: {
        previewPhoto() {
            return this.uploadedImageUrl || this.currentUser.photo || this.defaultAvatar;
        },
        showProfessionalData() {
            return [1, 3].includes(Number(this.completeData.user_type_id || this.currentUser.user_type_id));
        },
        professionalDetails() {
            if (Number(this.completeData.user_type_id || this.currentUser.user_type_id) === 1) {
                return [
                    { label: "Kartu ID", value: this.completeData.id_card_number, icon: "fas fa-id-badge" },
                    { label: "Nomor SIP", value: this.completeData.sip_number, icon: "fas fa-file-medical" },
                    { label: "Masa Berlaku SIP", value: this.completeData.sip_exp, icon: "fas fa-calendar-check" },
                    { label: "Poliklinik", value: this.completeData.polyclinic_name, icon: "fas fa-clinic-medical" },
                    { label: "Telepon", value: this.completeData.phone, icon: "fas fa-phone" },
                    { label: "Nomor Handphone", value: this.completeData.mobile_phone, icon: "fas fa-mobile-alt" },
                    { label: "Alamat", value: this.completeData.address, icon: "fas fa-map-marker-alt" },
                ];
            }
            return [
                { label: "Kartu ID", value: this.completeData.id_card_number, icon: "fas fa-id-badge" },
                { label: "Tempat Lahir", value: this.completeData.birt_place, icon: "fas fa-map-pin" },
                { label: "Tanggal Lahir", value: this.completeData.birt_date, icon: "fas fa-calendar-alt" },
                { label: "Departemen", value: this.completeData.department_name, icon: "fas fa-building" },
                { label: "Jabatan", value: this.completeData.position_name, icon: "fas fa-user-tie" },
                { label: "Telepon", value: this.completeData.phone, icon: "fas fa-phone" },
                { label: "Nomor Handphone", value: this.completeData.mobile_phone, icon: "fas fa-mobile-alt" },
                { label: "Alamat", value: this.completeData.address, icon: "fas fa-map-marker-alt" },
            ];
        },
    },
    mounted() {
        this.showCompleteData();
    },
    beforeDestroy() {
        this.revokeUploadedImage();
    },
    methods: {
        displayValue(value) {
            return value || "Belum tersedia";
        },
        useDefaultAvatar(event) {
            if (!event.target.src.endsWith(this.defaultAvatar)) event.target.src = this.defaultAvatar;
        },
        revokeUploadedImage() {
            if (!this.uploadedImageUrl) return;
            URL.revokeObjectURL(this.uploadedImageUrl);
            this.uploadedImageUrl = null;
        },
        onFileUpload(file) {
            this.revokeUploadedImage();
            if (!file) return;
            this.uploadedImageUrl = URL.createObjectURL(file);
        },
        async formOnSubmit() {
            this.isLoading = true;
            const formData = new FormData();
            Object.entries(this.form).forEach(([key, value]) => {
                if (value !== null && value !== "") formData.append(key, value);
            });

            try {
                const response = await module.submit(formData, `profile/change-profile/${this.currentUser.user_id}`);
                if (response.state === "error") {
                    this.error = validation.setValidationError(this.error, response.error.errors, response.error.message);
                    return;
                }

                Swal.fire(response.success.title, response.success.message, "success");
                const updatedUser = response.success.user.data;
                this.$store.dispatch(UPDATE_PERSONAL_INFO, updatedUser);
                setUser(JSON.stringify(updatedUser));
                this.$router.push("/profile/show");
            } finally {
                this.isLoading = false;
            }
        },
        async showCompleteData() {
            this.completeData = (await module.get("users/me")) || {};
        },
    },
};
</script>

<style lang="scss" src="@/assets/sass/pages/_profile-pages.scss"></style>
